/**
 * Local-only performance instrumentation for the Process Data page.
 *
 * This exists to produce before/after numbers for the Process Data performance
 * work. It is inert unless explicitly enabled (see `isPerfEnabled`), so it costs
 * a boolean check per call in normal use and never ships data anywhere.
 *
 * Read the results via `window.__pdPerf.snapshot()` in the console, or let the
 * Playwright harness in tests/playwright/perf pull them out.
 *
 * Deliberately zero-dependency and framework-free: it is imported from hooks,
 * services and components alike, and must not force a re-render or hold React
 * state.
 */

export type FetchTrigger = 'initial' | 'live-tail' | 'zoom' | 'other'

export type FetchOutcome = 'success' | 'aborted' | 'error'

export interface PerfFetchRecord {
  seq: number
  trigger: FetchTrigger
  /** Cache verdict at request time, from useTimeSeriesDataCache.getRequestStatus. */
  cacheStatus: string
  sensorCount: number
  rangeSeconds: number
  interval?: number
  intervalUnit?: string
  /** All timings are ms since page mount, so runs are comparable. */
  requestSentMs: number
  /** When the HTTP request actually left, after debounce and cache checks. */
  dispatchedMs?: number
  firstByteMs?: number
  lastByteMs?: number
  mergeDoneMs?: number
  /** Client-side delay before dispatch. Isolates the 500ms debounce tax. */
  queueMs?: number
  /** Server think time: dispatch -> first byte. */
  ttfbMs?: number
  /** Wire time: first byte -> last byte. Near-zero means streaming is not working. */
  streamMs?: number
  /** Main-thread cost of turning the response into chart rows. */
  mergeMs?: number
  /** Bytes after gzip decoding. Wire bytes need CDP; see the Playwright harness. */
  decodedBytes?: number
  pointCount?: number
  outcome?: FetchOutcome
  error?: string
}

export interface PerfSnapshot {
  scenario: string
  startedAt: string
  /** ms since page mount. */
  marks: Record<string, number>
  fetches: PerfFetchRecord[]
  counters: {
    /**
     * Times the full centered loading state appeared. This is the primary
     * acceptance metric for the live-tail thrash: it should be 1 on a cold load
     * and stay 1 no matter how long live tail runs.
     */
    centeredLoaderShows: number
    chartCleared: number
    uplotCreated: number
    uplotDestroyed: number
    emptyStateShows: number
  }
  longTasks: {
    count: number
    totalMs: number
    maxMs: number
  }
  /**
   * Main-thread cost of turning chartData into uPlot-shaped arrays
   * (generateUPlotData + the resample inside it), which runs in a render
   * useMemo AFTER firstDataReady. The fetch records' mergeMs stops at
   * firstDataReady, so without this the O(rows x pens) render-prep step was
   * invisible. Accumulated like longTasks because it reruns on every
   * data/config change, not once per fetch.
   */
  renderPrep: {
    count: number
    totalMs: number
    maxMs: number
  }
}

const PERF_FLAG_KEY = 'pd_perf'

let enabled: boolean | null = null

/**
 * Enabled from the console via `__pdPerf.enable()`; sticky in localStorage.
 * Checked once and cached: the flag is not meant to change mid-session.
 *
 * Deliberately NOT a query param. The Process Data routes validate search with
 * a zod object whose transform returns an explicit shape, so the router strips
 * anything it does not know about and rewrites the URL — `?pdPerf=1` was gone
 * before this ever read it, and the harness silently measured nothing.
 */
export const isPerfEnabled = (): boolean => {
  if (enabled !== null) return enabled
  if (typeof window === 'undefined') {
    enabled = false
    return enabled
  }
  try {
    enabled = window.localStorage.getItem(PERF_FLAG_KEY) === '1'
  } catch {
    // Private-mode storage access can throw; measurement is never worth a crash.
    enabled = false
  }
  return enabled
}

interface PerfState {
  scenario: string
  startedAt: string
  t0: number
  marks: Record<string, number>
  fetches: PerfFetchRecord[]
  counters: PerfSnapshot['counters']
  longTasks: PerfSnapshot['longTasks']
  renderPrep: PerfSnapshot['renderPrep']
  seq: number
  longTaskObserver: PerformanceObserver | null
}

const createState = (scenario: string): PerfState => ({
  scenario,
  startedAt: new Date().toISOString(),
  t0: performance.now(),
  marks: {},
  fetches: [],
  counters: {
    centeredLoaderShows: 0,
    chartCleared: 0,
    uplotCreated: 0,
    uplotDestroyed: 0,
    emptyStateShows: 0,
  },
  longTasks: { count: 0, totalMs: 0, maxMs: 0 },
  renderPrep: { count: 0, totalMs: 0, maxMs: 0 },
  seq: 0,
  longTaskObserver: null,
})

let state: PerfState = createState('default')

const since = (): number => Math.round(performance.now() - state.t0)

const observeLongTasks = (): void => {
  state.longTaskObserver?.disconnect()
  if (typeof PerformanceObserver === 'undefined') return
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        state.longTasks.count += 1
        state.longTasks.totalMs += Math.round(entry.duration)
        state.longTasks.maxMs = Math.max(
          state.longTasks.maxMs,
          Math.round(entry.duration)
        )
      }
    })
    observer.observe({ entryTypes: ['longtask'] })
    state.longTaskObserver = observer
  } catch {
    // longtask is not supported everywhere; the rest of the run is still useful.
  }
}

/**
 * Resets all counters and restarts the clock. The Playwright harness calls this
 * between scenarios so each run is independent.
 */
export const perfReset = (scenario: string): void => {
  if (!isPerfEnabled()) return
  state.longTaskObserver?.disconnect()
  state = createState(scenario)
  observeLongTasks()
}

/** Records a one-shot milestone. First write wins, so cold-load marks aren't overwritten by later renders. */
export const perfMark = (name: string): void => {
  if (!isPerfEnabled()) return
  if (state.marks[name] === undefined) {
    state.marks[name] = since()
  }
}

/** Accumulates one generateUPlotData/resample pass; see PerfSnapshot.renderPrep. */
export const perfRenderPrep = (durationMs: number): void => {
  if (!isPerfEnabled()) return
  const duration = Math.round(durationMs)
  state.renderPrep.count += 1
  state.renderPrep.totalMs += duration
  state.renderPrep.maxMs = Math.max(state.renderPrep.maxMs, duration)
}

export const perfCount = (name: keyof PerfSnapshot['counters']): void => {
  if (!isPerfEnabled()) return
  state.counters[name] += 1
}

export interface BeginFetchMeta {
  trigger: FetchTrigger
  cacheStatus: string
  sensorCount: number
  rangeSeconds: number
  interval?: number
  intervalUnit?: string
}

/** Returns a handle to correlate the later firstByte/done calls, or -1 when disabled. */
export const perfBeginFetch = (meta: BeginFetchMeta): number => {
  if (!isPerfEnabled()) return -1
  state.seq += 1
  state.fetches.push({
    seq: state.seq,
    trigger: meta.trigger,
    cacheStatus: meta.cacheStatus,
    sensorCount: meta.sensorCount,
    rangeSeconds: Math.round(meta.rangeSeconds),
    interval: meta.interval,
    intervalUnit: meta.intervalUnit,
    requestSentMs: since(),
  })
  return state.seq
}

const findFetch = (seq: number): PerfFetchRecord | undefined =>
  seq < 0 ? undefined : state.fetches.find((record) => record.seq === seq)

/** Called immediately before the HTTP request leaves, so debounce is not counted as server time. */
export const perfFetchDispatched = (seq: number): void => {
  if (!isPerfEnabled()) return
  const record = findFetch(seq)
  if (!record || record.dispatchedMs !== undefined) return
  record.dispatchedMs = since()
  record.queueMs = record.dispatchedMs - record.requestSentMs
}

export const perfFetchFirstByte = (seq: number): void => {
  if (!isPerfEnabled()) return
  const record = findFetch(seq)
  if (!record || record.firstByteMs !== undefined) return
  record.firstByteMs = since()
  record.ttfbMs =
    record.firstByteMs - (record.dispatchedMs ?? record.requestSentMs)
}

export const perfFetchLastByte = (seq: number, decodedBytes: number): void => {
  if (!isPerfEnabled()) return
  const record = findFetch(seq)
  if (!record) return
  record.lastByteMs = since()
  record.decodedBytes = decodedBytes
  if (record.firstByteMs !== undefined) {
    record.streamMs = record.lastByteMs - record.firstByteMs
  }
}

export const perfFetchDone = (
  seq: number,
  result: { outcome: FetchOutcome; pointCount?: number; error?: string }
): void => {
  if (!isPerfEnabled()) return
  const record = findFetch(seq)
  if (!record) return
  record.outcome = result.outcome
  record.pointCount = result.pointCount
  record.error = result.error
  record.mergeDoneMs = since()
  const mergeFrom = record.lastByteMs ?? record.requestSentMs
  record.mergeMs = record.mergeDoneMs - mergeFrom
}

export const perfSnapshot = (): PerfSnapshot => ({
  scenario: state.scenario,
  startedAt: state.startedAt,
  marks: { ...state.marks },
  fetches: state.fetches.map((record) => ({ ...record })),
  counters: { ...state.counters },
  longTasks: { ...state.longTasks },
  renderPrep: { ...state.renderPrep },
})

declare global {
  interface Window {
    __pdPerf?: {
      enable: () => void
      disable: () => void
      snapshot: () => PerfSnapshot
      reset: (scenario: string) => void
      enabled: () => boolean
    }
  }
}

/**
 * Always exposed, so the toggle is reachable before instrumentation is on --
 * otherwise enabling it would require it to already be enabled.
 */
if (typeof window !== 'undefined') {
  if (isPerfEnabled()) observeLongTasks()
  window.__pdPerf = {
    enable: () => {
      window.localStorage.setItem(PERF_FLAG_KEY, '1')
      window.location.reload()
    },
    disable: () => {
      window.localStorage.removeItem(PERF_FLAG_KEY)
      window.location.reload()
    },
    snapshot: perfSnapshot,
    reset: perfReset,
    enabled: isPerfEnabled,
  }
}
